import { useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Card } from "@/components/Common";

const matches = (value, allowed) => (Array.isArray(value) ? value : [value]).some((v) => allowed.includes(v));

const isVisible = (q, answers, byId) =>
  Object.entries(q.show_if || {}).every(([p, allowed]) => isVisible(byId[p], answers, byId) && matches(answers[p], allowed));

function ContextQuestion({ q, value, onChange }) {
  const toggle = (opt) => {
    if (q.type === "single") return onChange(value === opt ? "" : opt);
    const cur = Array.isArray(value) ? value : [];
    onChange(cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt]);
  };
  const on = (opt) => (q.type === "single" ? value === opt : Array.isArray(value) && value.includes(opt));
  return (
    <div className="animate-in fade-in" data-testid={`context-q-${q.id}`}>
      <p className="text-sm font-semibold text-[#0D2517]">{q.text}{q.required && <span className="text-[#B45309]"> *</span>}</p>
      {q.hint && <p className="mt-0.5 text-xs text-[#5b6b60]">{q.hint}</p>}
      {q.type === "text" ? (
        <textarea className="field mt-2 min-h-[56px]" value={value || ""} maxLength={500} onChange={(e) => onChange(e.target.value)}
          data-testid={`context-answer-${q.id}`} placeholder="Scrivi qui (oppure lascia vuoto se non lo sai)" />
      ) : (
        <div className="mt-2 flex flex-wrap gap-2">
          {q.options.map((opt, i) => (
            <button key={opt} type="button" onClick={() => toggle(opt)} className={`chip ${on(opt) ? "chip-on" : ""}`}
              data-testid={`context-answer-${q.id}-opt-${i}`}>{opt}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function Section({ section, answers, byId, setAnswer }) {
  const visible = section.questions.filter((q) => isVisible(q, answers, byId));
  return (
    <Card testid={`context-section-${section.id}`}>
      <p className="mb-4 font-serif text-xl font-semibold text-[#0D2517]">{section.title}</p>
      <div className="space-y-5">
        {visible.map((q) => <ContextQuestion key={q.id} q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />)}
      </div>
    </Card>
  );
}

export default function ContextStep({ analysis, reload }) {
  const form = analysis.context_form || [];
  const byId = Object.fromEntries(form.flatMap((s) => s.questions.map((q) => [q.id, q])));
  const [answers, setAnswers] = useState(analysis.context || {});
  const [busy, setBusy] = useState(false);
  const setAnswer = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));
  const missing = Object.values(byId).filter((q) => q.required && isVisible(q, answers, byId) && !answers[q.id]);

  const submit = async () => {
    if (missing.length) return toast.error(`Rispondi a: ${missing.map((q) => q.text).join("; ")}`);
    const visibleAnswers = Object.fromEntries(Object.entries(answers).filter(([k]) => byId[k] && isVisible(byId[k], answers, byId)));
    setBusy(true);
    try {
      await api.post(`/analyses/${analysis.id}/context`, { answers: visibleAnswers });
      reload();
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 px-5 py-6" data-testid="context-step">
      <div>
        <p className="eyebrow">Passo 3 · Contesto della pianta</p>
        <h1 className="font-serif text-3xl font-semibold text-[#0D2517]">Raccontami come vive la tua pianta</h1>
        <p className="mt-1 text-sm text-[#5b6b60]">Queste informazioni servono a capire la causa e a costruire una cura adatta proprio a questa pianta. Se non conosci un dato scegli "Non lo so" o lascia vuoto. Le domande con * sono obbligatorie.</p>
      </div>
      {form.map((s) => <Section key={s.id} section={s} answers={answers} byId={byId} setAnswer={setAnswer} />)}
      <button onClick={submit} disabled={busy} className="btn-primary w-full" data-testid="context-submit-button">
        <Send size={16} /> CONTINUA
      </button>
    </div>
  );
}
